import * as React from 'react';

import AdminPermission from 'bundles/admin/utils/AdminPermission';
import { useNaptime } from 'bundles/naptimejs';
import type { AdminUserPermissionsV1 as AdminUserPermissionsV1Type } from 'bundles/naptimejs/resources/__generated__/AdminUserPermissionsV1';
import AdminUserPermissionsV1 from 'bundles/naptimejs/resources/adminUserPermissionsV1';

export interface UseAdminPermissionsResponse {
  loading: boolean;
  permissions?: AdminPermission;
}

type NetworkStatus = 'notYetRequested' | 'inFlight' | 'completed';

/** A wrapper for useNaptime -> AdminUserPermissions -> AdminPermission class instantiation */
const useAdminPermissions = (): UseAdminPermissionsResponse => {
  const { pending, ...result } = useNaptime<{ adminUserPermissions: AdminUserPermissionsV1Type[] }>(() => ({
    adminUserPermissions: AdminUserPermissionsV1.finder('my', { params: {} }),
  }));

  /*
    The first useNaptime result has pending false and the data is undefined.
    To align with Apollo's behaviour, we consider the state of 
    "not yet requested" to be loading true, even if a network request hasn't yet been sent

    This introduces an extra render where {loading: true, data: !undefined}, but it's the lesser of two evils.
  */
  const [networkStatus, setNetworkStatus] = React.useState<NetworkStatus>('notYetRequested');
  React.useEffect(() => {
    if (networkStatus === 'notYetRequested' && pending) {
      setNetworkStatus('inFlight');
    } else if (networkStatus === 'inFlight' && !pending) {
      setNetworkStatus('completed');
    }
  }, [networkStatus, pending]);

  const permissionsFromNaptime = result.data.adminUserPermissions?.[0]?.permissions;
  // If we already have the data from cache, we don't need to consider that the request is pending
  const loading = networkStatus !== 'completed' && !permissionsFromNaptime;
  const permissions = permissionsFromNaptime ? new AdminPermission(permissionsFromNaptime) : undefined;

  return {
    loading,
    permissions,
  };
};

export default useAdminPermissions;
